import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, leaveSchema } from "@utils/index";
import { LeaveFormInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { useUserLeave } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  MobileHeader,
  Form,
  FormRow,
  FormText,
  Button,
  ConfirmModal,
} from "@components/index";

export const LeaveForm = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);

  const [showConfirm, setShowConfirm] = useState(false);

  const { mutate: mutateLeave, isPending: isPendingUserLeave } = useUserLeave(
    () => {
      reset();
      notify(
        "success",
        "Your feedback has been submitted. We will be in touch soon.",
      );
    },
    ({ message, fields }) => {
      Object.keys(fields || {}).forEach((key) =>
        setError(key as "message", { message: fields![key] }),
      );
      notify("error", message);
    },
  );

  useEffect(() => {
    setState({ isLoading: isPendingUserLeave });
  }, [isPendingUserLeave, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    getValues,
    setError,
  } = useForm<LeaveFormInterface>({
    mode: "onBlur",
    disabled: isPendingUserLeave,
    resolver: yupResolver(leaveSchema),
  });

  const handleSend = () => {
    setShowConfirm(false);
    const data = getValues();
    mutateLeave({ ...data });
  };

  return (
    <>
      {isMobile && <MobileHeader href="/profile" title="Leave FBTC" />}
      {!isMobile && (
        <div className="max-w-[552px] px-14 pt-12 text-400 font-bold text-black-100">
          Leave FBTC
        </div>
      )}
      <div className="px-4 pt-10 text-center text-black-54 md:max-w-[552px] md:px-14 md:pt-2 md:text-left">
        We are sorry to see that you wish to leave FBTC. We would be grateful if
        you would let us know why you intend to leave and a member of our team
        may contact you to discuss.
      </div>
      <Form
        onSubmit={handleSubmit(() => setShowConfirm(true))}
        className="px-4 text-black-100 md:max-w-[552px] md:px-14"
      >
        <FormRow top={isMobile ? "small" : "large"}>
          <FormText
            {...register<"message">("message")}
            name="message"
            label="Reason for Leaving"
            error={formState.errors.message?.message}
            onChange={() => clearErrors("message")}
          />
        </FormRow>
        <FormRow isRow isBetween top="large">
          <Button type="submit" title="Submit" className="w-full" />
        </FormRow>
        <ConfirmModal
          show={showConfirm}
          confirmButtonType="submit"
          confirmButtonTitle="Yes, Send"
          text1="Are you sure?"
          text2="Please note that by indicating your intention to leave we will restrict your access to our software. You can request to export your data but will no longer be able to add or amend transactions."
          onClickCancel={() => setShowConfirm(false)}
          onClickConfirm={handleSend}
        />
      </Form>
    </>
  );
};
